import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import { TbSend, TbTrash } from 'react-icons/tb';
import classNames from 'classnames';
import { Post, PostComment } from '../../../types/post';
import postService from '../../../services/community/postService';
import useUserId from '../../../hooks/account/useUserId';
import useLanguage from '../../../hooks/account/useLanguage';
import Language from '../../../types/language';
import Avatar from '../misc/Avatar';
import Input from '../misc/Input';

interface PostCommentsSectionProps {
  post: Post;
  expanded: boolean;
}

export default function PostCommentsSection({
  post,
  expanded,
}: PostCommentsSectionProps): JSX.Element | null {
  const { t } = useTranslation();
  const currentUserId = useUserId();
  const lang = useLanguage();
  const locale = lang === Language.NL ? nl : enGB;

  const [comments, setComments] = useState<PostComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [loaded, setLoaded] = useState(false);

  const loadComments = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await postService.getComments(post.postId);
      setComments(data);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [post.postId]);

  useEffect(() => {
    if (expanded && !loaded) {
      loadComments();
    }
  }, [expanded, loaded, loadComments]);

  const handleSubmit = async (): Promise<void> => {
    const content = commentText.trim();
    if (!content || submitting) return;
    setSubmitting(true);
    try {
      const created = await postService.addComment(post.postId, { content });
      setComments((prev) => [...prev, created]);
      setCommentText('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment: PostComment): Promise<void> => {
    await postService.deleteComment(post.postId, comment.commentId);
    setComments((prev) => prev.filter((c) => c.commentId !== comment.commentId));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!expanded) return null;

  return (
    <div className="border-t border-secondary-200 bg-secondary-100/50 px-4 py-3">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary-600">
        {t('page.communicationOverview.posts.comments.title', { count: post.commentsCount })}
      </p>

      {loading ? (
        <p className="py-2 text-center text-sm text-primary-600">{t('general.pending')}</p>
      ) : comments.length === 0 ? (
        <p className="py-2 text-center text-sm text-primary-600">
          {t('page.communicationOverview.posts.comments.empty')}
        </p>
      ) : (
        <ul className="mb-3 flex max-h-64 flex-col gap-3 overflow-y-auto">
          {comments.map((comment) => {
            const authorName = `${comment.author.firstName} ${comment.author.lastName}`;
            const canDelete = Boolean(currentUserId);
            const timeAgo = formatDistanceToNow(new Date(comment.createdAt), {
              addSuffix: true,
              locale,
            });

            return (
              <li key={comment.commentId} className="flex items-start gap-2">
                <Avatar
                  alias={authorName}
                  src={comment.author.picture ?? undefined}
                  className="h-8 w-8 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-primary-900">{authorName}</p>
                    <p className="shrink-0 text-xs text-primary-600">{timeAgo}</p>
                  </div>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-primary-800">
                    {comment.content}
                  </p>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(comment)}
                    className="shrink-0 rounded p-1 text-primary-600 hover:bg-secondary-200 hover:text-danger"
                    aria-label={t('page.communicationOverview.posts.comments.delete')}>
                    <TbTrash className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {currentUserId && (
        <div className="flex items-end gap-2">
          <Input
            id={`post-comment-${post.postId}`}
            type="textarea"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('page.communicationOverview.posts.comments.placeholder')}
            className="min-h-[4rem] flex-1 resize-none rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !commentText.trim()}
            className={classNames(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-secondary-50 transition-opacity',
              !commentText.trim() && 'opacity-40',
            )}
            aria-label={t('general.send')}>
            <TbSend className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
